import { Add } from '@carbon/icons-react';
import { Tooltip as CarbonTooltip } from '@carbon/react';
import { ActionIcon, TextInput } from '@mantine/core';
import { useCallback, useMemo, useState } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import cx from 'classnames';

import styles from './TagsInput.module.scss';

import sharedMessages from 'Components/utils/sharedMessages';
import TagRemovalConfirmation from 'Components/Common/TagsInput/TagRemovalConfirmation/TagRemovalConfirmation';
import { InputElement } from 'Components/Common/TagsInput/types';
import Tag from '../Tag/Tag';

type Props = {
    messages: {
        title: MessageDescriptor;
        titlePlaceholder: MessageDescriptor;
        elementAlreadyExist: MessageDescriptor;
        formName: MessageDescriptor;
        elementTooLong?: MessageDescriptor;
        elementAdded?: MessageDescriptor;
    },
    elements: InputElement[];
    textInputId: string;
    onAdd: (newName: string) => void | Promise<void>;
    onRemove: (toRemove: InputElement | null) => void;
    isDisabled?: boolean;
    disabledTooltip?: string;
    onClick?: (id?: string) => void;
    maxLength?: number;
}

const TagsInput = ({
    messages,
    elements,
    textInputId,
    onAdd,
    onRemove,
    isDisabled,
    disabledTooltip,
    onClick,
    maxLength,
}: Props) => {
    const intl = useIntl();
    const elementsSet = useMemo(
        () => new Set(elements.map((cs) => cs.name.trim().toLocaleLowerCase())),
        [elements],
    );
    const [newElementName, setNewElementName] = useState('');
    const [errorText, setErrorText] = useState('');
    const [successText, setSuccessText] = useState('');
    const trimmedElementName = newElementName.trim();
    const isTooLong = maxLength !== undefined && trimmedElementName.length > maxLength;
    const isDisabledSubmit = trimmedElementName.length < 3 || isTooLong;
    const [selectToRemove, setSelectedToRemove] = useState<InputElement | null>(null);
    const removalRequest = selectToRemove !== null;
    const lengthErrorText = isTooLong && messages.elementTooLong
        ? intl.formatMessage(messages.elementTooLong, { max: maxLength })
        : '';

    const onChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setNewElementName(event.target.value);
        setErrorText('');
        setSuccessText('');
    }, []);

    const submitElement = useCallback(async () => {
        if (isDisabledSubmit) {
            if (isTooLong && messages.elementTooLong) {
                setErrorText(intl.formatMessage(messages.elementTooLong, { max: maxLength }));
            }
            return;
        }

        if (elementsSet.has(trimmedElementName.toLocaleLowerCase())) {
            setErrorText(intl.formatMessage(messages.elementAlreadyExist));
            setSuccessText('');
            return;
        }

        await onAdd(trimmedElementName);
        setNewElementName('');
        setErrorText('');
        setSuccessText(messages.elementAdded ? intl.formatMessage(messages.elementAdded) : '');
    }, [
        elementsSet,
        intl,
        isDisabledSubmit,
        isTooLong,
        maxLength,
        messages.elementAdded,
        messages.elementAlreadyExist,
        messages.elementTooLong,
        onAdd,
        trimmedElementName,
    ]);

    const onConfirm = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        submitElement().catch(() => undefined);
    }, [submitElement]);

    const onInputKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key !== 'Enter') {
            return;
        }

        // Prevent CreateFullPage and other parent handlers from treating Enter as a submit/navigation action.
        event.preventDefault();
        event.stopPropagation();

        submitElement().catch(() => undefined);
    }, [submitElement]);

    const onButtonClick = useCallback(() => {
        submitElement().catch(() => undefined);
    }, [submitElement]);

    const onCloseRemovalRequest = useCallback(() => {
        setSelectedToRemove(null);
    }, []);

    const onRemovalRequest = useCallback((element: InputElement) => {
        setSelectedToRemove(element);
    }, []);

    const form = (
        <form aria-label={intl.formatMessage(messages.formName)} onSubmit={onConfirm}>
            <div className={cx(styles.textAndButton, errorText && styles.textAndButtonRepeated)}>
                <TextInput
                    id={textInputId}
                    label={intl.formatMessage(messages.title)}
                    onChange={onChange}
                    onKeyDown={onInputKeyDown}
                    placeholder={intl.formatMessage(messages.titlePlaceholder)}
                    error={errorText || lengthErrorText || undefined}
                    disabled={isDisabled}
                    value={newElementName}
                    type="text"
                    radius="md"
                    maxLength={maxLength ? maxLength + 1 : undefined}
                />
                <ActionIcon
                    disabled={isDisabledSubmit || isDisabled}
                    onClick={onButtonClick}
                    aria-label={intl.formatMessage(sharedMessages.add)}
                    radius="md"
                    size={44}
                    variant="filled"
                >
                    <Add size={20} />
                </ActionIcon>
            </div>
            {successText && <p className={styles.successText}>{successText}</p>}
            <div className={styles.elements}>
                {elements.map((element) => ((
                    <Tag
                        dismissible={!element.isRequired}
                        disabled={isDisabled}
                        color={element.color}
                        onClick={() => { onClick?.(element.id); }}
                        onRemove={() => { onRemovalRequest(element); }}
                        key={element.id}
                    >
                        {element.name}
                    </Tag>
                )
                ))}
            </div>
        </form>
    );

    return (
        <>
            {isDisabled && disabledTooltip ? (
                <CarbonTooltip
                    label={<span className={styles.disabledTooltipLabel}>{disabledTooltip}</span>}
                    align="bottom"
                >
                    <div className={styles.disabledTooltipWrapper}>
                        {form}
                    </div>
                </CarbonTooltip>
            ) : form}
            <TagRemovalConfirmation
                tag={selectToRemove}
                onSubmit={onRemove}
                onClose={onCloseRemovalRequest}
                isOpen={removalRequest}
            />
        </>
    );
};

export default TagsInput;
